import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, Box, Typography,
  FormControl, InputLabel, Select, MenuItem, Checkbox,
  List, ListItem, ListItemIcon, ListItemText, ListItemAvatar,
  Avatar, CircularProgress, Alert, Chip, Stack,
  FormControlLabel, Switch, Autocomplete, TextField,
  Card, CardContent, Divider, LinearProgress
} from '@mui/material';
import {
  CloudDownload, SportsSoccer, Groups, Person,
  Check, ArrowBack, ArrowForward
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';

const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || '';

/**
 * Dialog d'import depuis la base de référence
 *
 * @param {boolean} open - Si le dialog est ouvert
 * @param {function} onClose - Callback de fermeture
 * @param {string} mode - 'ligue' ou 'equipes'
 * @param {array} niveaux - Liste des niveaux disponibles
 * @param {array} competitions - Liste des compétitions disponibles (pour mode equipes)
 * @param {function} onImportComplete - Callback après import réussi
 */
const ImportFromReference = ({
  open,
  onClose,
  mode = 'ligue',
  niveaux = [],
  competitions = [],
  onImportComplete
}) => {
  const { t } = useTranslation();

  // States communs
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importJoueurs, setImportJoueurs] = useState(true);

  // States pour mode ligue
  const [ligues, setLigues] = useState([]);
  const [selectedLigue, setSelectedLigue] = useState(null);
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [competitionNom, setCompetitionNom] = useState('');
  const [ligueEquipes, setLigueEquipes] = useState([]);

  // States pour mode equipes
  const [allEquipes, setAllEquipes] = useState([]);
  const [selectedEquipes, setSelectedEquipes] = useState([]);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  const [equipeJoueurs, setEquipeJoueurs] = useState({});

  // Charger les ligues au montage (mode ligue)
  useEffect(() => {
    if (open && mode === 'ligue') {
      fetchLigues();
    }
  }, [open, mode]);

  // Charger les équipes au montage (mode equipes)
  useEffect(() => {
    if (open && mode === 'equipes') {
      fetchAllEquipes();
    }
  }, [open, mode]);

  // Charger les équipes quand une ligue est sélectionnée
  useEffect(() => {
    if (selectedLigue) {
      fetchLigueEquipes(selectedLigue.id);
      setCompetitionNom(selectedLigue.nom);
    }
  }, [selectedLigue]);

  // Reset au changement de mode ou fermeture
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedLigue(null);
      setSelectedNiveau('');
      setCompetitionNom('');
      setLigueEquipes([]);
      setSelectedEquipes([]);
      setSelectedCompetitions([]);
      setEquipeJoueurs({});
      setError('');
      setImportResult(null);
    }
  }, [open]);

  const fetchLigues = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/comp/ref/ligues');
      setLigues(data);
    } catch (err) {
      setError(t('competitions.import.errorLoadingLigues'));
    }
    setLoading(false);
  };

  const fetchAllEquipes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/comp/ref/equipes');
      setAllEquipes(data);
    } catch (err) {
      setError(t('competitions.import.errorLoadingEquipes'));
    }
    setLoading(false);
  };

  const fetchLigueEquipes = async (ligueId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/comp/ref/ligues/${ligueId}/equipes`);
      setLigueEquipes(data);
    } catch (err) {
      setError(t('competitions.import.errorLoadingEquipes'));
    }
    setLoading(false);
  };

  const fetchEquipeJoueurs = async (equipeId) => {
    try {
      const { data } = await api.get(`/api/comp/ref/equipes/${equipeId}/joueurs`);
      setEquipeJoueurs(prev => ({ ...prev, [equipeId]: data }));
    } catch (err) {
      console.error('Error loading joueurs:', err);
    }
  };

  const handleImportLigue = async () => {
    if (!selectedLigue || !selectedNiveau) {
      setError(t('competitions.import.selectLigueAndNiveau'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/api/comp/import/ligue', {
        ref_ligue_id: selectedLigue.id,
        niveau_id: selectedNiveau,
        competition_nom: competitionNom || selectedLigue.nom,
        import_joueurs: importJoueurs
      });

      setImportResult(data.stats);
      setActiveStep(mode === 'ligue' ? 2 : 3);

      if (onImportComplete) {
        onImportComplete(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('competitions.import.errorImport'));
    }

    setLoading(false);
  };

  const handleImportEquipes = async () => {
    if (selectedEquipes.length === 0 || selectedCompetitions.length === 0) {
      setError(t('competitions.import.selectEquipesAndCompetitions'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/api/comp/import/equipes', {
        ref_equipe_ids: selectedEquipes.map(e => e.id),
        competition_ids: selectedCompetitions.map(c => c.id),
        import_joueurs: importJoueurs
      });

      setImportResult(data.stats);
      setActiveStep(3);

      if (onImportComplete) {
        onImportComplete(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('competitions.import.errorImport'));
    }

    setLoading(false);
  };

  const getRefLogoUrl = (type, id) => {
    return `${MEDIA_BASE_URL}/reference/${type}/${id}.png`;
  };

  // Steps pour mode ligue
  const ligueSteps = [
    t('competitions.import.steps.selectLigue'),
    t('competitions.import.steps.preview'),
    t('competitions.import.steps.result')
  ];

  // Steps pour mode equipes
  const equipesSteps = [
    t('competitions.import.steps.selectEquipes'),
    t('competitions.import.steps.selectCompetitions'),
    t('competitions.import.steps.preview'),
    t('competitions.import.steps.result')
  ];

  const steps = mode === 'ligue' ? ligueSteps : equipesSteps;

  // Rendu étape 1 mode ligue: Sélection de la ligue
  const renderLigueSelection = () => (
    <Box sx={{ minHeight: 400 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('competitions.import.selectLigueDesc')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Autocomplete
            options={ligues}
            getOptionLabel={(option) => `${option.nom} (${option.pays || 'N/A'})`}
            groupBy={(option) => option.pays || 'Autre'}
            value={selectedLigue}
            onChange={(_, newValue) => setSelectedLigue(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('competitions.import.selectLigue')}
                variant="outlined"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Avatar
                  src={getRefLogoUrl('ligues', option.id)}
                  sx={{ width: 24, height: 24, mr: 1 }}
                >
                  <SportsSoccer fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="body2">{option.nom}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.nb_equipes} {t('competitions.import.teams')} - {option.nb_joueurs} {t('competitions.import.players')}
                  </Typography>
                </Box>
              </li>
            )}
            sx={{ mb: 3 }}
          />

          {selectedLigue && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('competitions.import.targetNiveau')}</InputLabel>
                <Select
                  value={selectedNiveau}
                  onChange={(e) => setSelectedNiveau(e.target.value)}
                  label={t('competitions.import.targetNiveau')}
                >
                  {niveaux.map((niveau) => (
                    <MenuItem key={niveau.id} value={niveau.id}>
                      {niveau.nom}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label={t('competitions.import.competitionName')}
                value={competitionNom}
                onChange={(e) => setCompetitionNom(e.target.value)}
                helperText={t('competitions.import.leaveEmptyForDefault')}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={importJoueurs}
                    onChange={(e) => setImportJoueurs(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('competitions.import.importPlayers')}
              />
            </>
          )}
        </>
      )}
    </Box>
  );

  // Rendu preview pour mode ligue
  const renderLiguePreview = () => (
    <Box sx={{ minHeight: 400 }}>
      <Card sx={{ mb: 2, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={getRefLogoUrl('ligues', selectedLigue?.id)}
              sx={{ width: 60, height: 60 }}
            >
              <SportsSoccer />
            </Avatar>
            <Box>
              <Typography variant="h6">{competitionNom || selectedLigue?.nom}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('competitions.import.targetNiveau')}: {niveaux.find(n => n.id === selectedNiveau)?.nom}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  icon={<Groups />}
                  label={`${ligueEquipes.length} ${t('competitions.import.teams')}`}
                  size="small"
                />
                {importJoueurs && (
                  <Chip
                    icon={<Person />}
                    label={`${ligueEquipes.reduce((acc, e) => acc + (e.nb_joueurs || 0), 0)} ${t('competitions.import.players')}`}
                    size="small"
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('competitions.import.teamsToImport')}:
      </Typography>

      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        <List dense>
          {ligueEquipes.map((equipe) => (
            <ListItem key={equipe.id}>
              <ListItemAvatar>
                <Avatar
                  src={getRefLogoUrl('equipes', equipe.id)}
                  sx={{ width: 32, height: 32 }}
                >
                  <Groups />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={equipe.nom}
                secondary={importJoueurs ? `${equipe.nb_joueurs || 0} ${t('competitions.import.players')}` : null}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {loading && <LinearProgress sx={{ mt: 2 }} />}
    </Box>
  );

  // Rendu étape 1 mode equipes: Sélection des équipes
  const renderEquipesSelection = () => (
    <Box sx={{ minHeight: 400 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('competitions.import.selectEquipesDesc')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Autocomplete
          multiple
          options={allEquipes}
          getOptionLabel={(option) => option.nom}
          groupBy={(option) => `${option.pays} - ${option.ligue_nom}`}
          value={selectedEquipes}
          onChange={(_, newValue) => setSelectedEquipes(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('competitions.import.selectEquipes')}
              variant="outlined"
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              <Avatar
                src={getRefLogoUrl('equipes', option.id)}
                sx={{ width: 24, height: 24, mr: 1 }}
              >
                <Groups fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2">{option.nom}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.nb_joueurs} {t('competitions.import.players')}
                </Typography>
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                avatar={
                  <Avatar src={getRefLogoUrl('equipes', option.id)}>
                    <Groups />
                  </Avatar>
                }
                label={option.nom}
                {...getTagProps({ index })}
                key={option.id}
              />
            ))
          }
          sx={{ mb: 2 }}
        />
      )}

      {selectedEquipes.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {selectedEquipes.length} {t('competitions.import.teamsSelected')}
        </Alert>
      )}
    </Box>
  );

  // Rendu étape 2 mode equipes: Sélection des compétitions
  const renderCompetitionsSelection = () => (
    <Box sx={{ minHeight: 400 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('competitions.import.selectCompetitionsDesc')}
      </Typography>

      <Autocomplete
        multiple
        options={competitions}
        getOptionLabel={(option) => `${option.nom} (${option.niveau_nom})`}
        groupBy={(option) => option.niveau_nom}
        value={selectedCompetitions}
        onChange={(_, newValue) => setSelectedCompetitions(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('competitions.import.selectCompetitions')}
            variant="outlined"
          />
        )}
        sx={{ mb: 3 }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={importJoueurs}
            onChange={(e) => setImportJoueurs(e.target.checked)}
            color="primary"
          />
        }
        label={t('competitions.import.importPlayers')}
      />

      {selectedCompetitions.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('competitions.import.teamsWillBeAssociated', {
            teams: selectedEquipes.length,
            competitions: selectedCompetitions.length
          })}
        </Alert>
      )}
    </Box>
  );

  // Rendu preview pour mode equipes
  const renderEquipesPreview = () => (
    <Box sx={{ minHeight: 400 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('competitions.import.summary')}:
      </Typography>

      <Card sx={{ mb: 2, backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography variant="h4" color="primary">
                {selectedEquipes.length}
              </Typography>
              <Typography variant="caption">{t('competitions.import.teams')}</Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="primary">
                {selectedCompetitions.length}
              </Typography>
              <Typography variant="caption">{t('competitions.import.competitions')}</Typography>
            </Box>
            {importJoueurs && (
              <Box>
                <Typography variant="h4" color="primary">
                  {selectedEquipes.reduce((acc, e) => acc + (e.nb_joueurs || 0), 0)}
                </Typography>
                <Typography variant="caption">{t('competitions.import.players')}</Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('competitions.import.teamsToImport')}:
      </Typography>

      <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {selectedEquipes.map((equipe) => (
            <Chip
              key={equipe.id}
              avatar={
                <Avatar src={getRefLogoUrl('equipes', equipe.id)}>
                  <Groups />
                </Avatar>
              }
              label={equipe.nom}
            />
          ))}
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('competitions.import.targetCompetitions')}:
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        {selectedCompetitions.map((comp) => (
          <Chip
            key={comp.id}
            label={`${comp.nom} (${comp.niveau_nom})`}
            color="primary"
            variant="outlined"
          />
        ))}
      </Stack>

      {loading && <LinearProgress sx={{ mt: 2 }} />}
    </Box>
  );

  // Rendu du résultat
  const renderResult = () => (
    <Box sx={{ minHeight: 400, textAlign: 'center', pt: 4 }}>
      {importResult ? (
        <>
          <Check sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {t('competitions.import.success')}
          </Typography>

          <Card sx={{ maxWidth: 400, mx: 'auto', mt: 3 }}>
            <CardContent>
              <Stack spacing={2}>
                {mode === 'ligue' && importResult.competition_nom && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('competitions.import.competitionCreated')}
                    </Typography>
                    <Typography variant="h6">{importResult.competition_nom}</Typography>
                  </Box>
                )}

                <Divider />

                <Stack direction="row" justifyContent="space-around">
                  <Box>
                    <Typography variant="h4" color="primary">
                      {importResult.equipes_importees}
                    </Typography>
                    <Typography variant="caption">{t('competitions.import.teamsImported')}</Typography>
                  </Box>
                  {importResult.equipes_existantes > 0 && (
                    <Box>
                      <Typography variant="h4" color="text.secondary">
                        {importResult.equipes_existantes}
                      </Typography>
                      <Typography variant="caption">{t('competitions.import.teamsExisting')}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h4" color="primary">
                      {importResult.joueurs_importes}
                    </Typography>
                    <Typography variant="caption">{t('competitions.import.playersImported')}</Typography>
                  </Box>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-around">
                  <Box>
                    <Typography variant="body1">{importResult.logos_copies}</Typography>
                    <Typography variant="caption">{t('competitions.import.logosCopied')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body1">{importResult.photos_copiees}</Typography>
                    <Typography variant="caption">{t('competitions.import.photosCopied')}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );

  // Rendu du contenu selon l'étape
  const renderStepContent = () => {
    if (mode === 'ligue') {
      switch (activeStep) {
        case 0:
          return renderLigueSelection();
        case 1:
          return renderLiguePreview();
        case 2:
          return renderResult();
        default:
          return null;
      }
    } else {
      switch (activeStep) {
        case 0:
          return renderEquipesSelection();
        case 1:
          return renderCompetitionsSelection();
        case 2:
          return renderEquipesPreview();
        case 3:
          return renderResult();
        default:
          return null;
      }
    }
  };

  // Validation pour passer à l'étape suivante
  const canProceed = () => {
    if (mode === 'ligue') {
      if (activeStep === 0) {
        return selectedLigue && selectedNiveau;
      }
    } else {
      if (activeStep === 0) {
        return selectedEquipes.length > 0;
      }
      if (activeStep === 1) {
        return selectedCompetitions.length > 0;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (mode === 'ligue' && activeStep === 1) {
      handleImportLigue();
    } else if (mode === 'equipes' && activeStep === 2) {
      handleImportEquipes();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const isLastStep = mode === 'ligue' ? activeStep === 2 : activeStep === 3;
  const isImportStep = mode === 'ligue' ? activeStep === 1 : activeStep === 2;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CloudDownload color="primary" />
          <Typography variant="h6">
            {mode === 'ligue'
              ? t('competitions.import.titleLigue')
              : t('competitions.import.titleEquipes')
            }
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {isLastStep ? t('common.close') : t('common.cancel')}
        </Button>

        {!isLastStep && (
          <>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || loading}
              startIcon={<ArrowBack />}
            >
              {t('common.back')}
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed() || loading}
              endIcon={isImportStep ? <CloudDownload /> : <ArrowForward />}
              sx={{ backgroundColor: '#ff9800' }}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : isImportStep ? (
                t('competitions.import.startImport')
              ) : (
                t('common.next')
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportFromReference;
